import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SaveIcon from "@mui/icons-material/Save";
import { toast } from "react-toastify";
import CategoryTreeSelect from "./CategoryTreeSelect";

export const CREATE_TARGET_LABELS = { mock_exam: "Mock Exam", paper: "Past Paper", worksheet: "Worksheet" };

/**
 * Validates the destination form; shows a toast and returns false when invalid.
 * Exported so callers can gate their submit handler on it.
 */
export const validateDestinationForm = (destination, form) => {
  if (!String(form.name || "").trim()) {
    toast.warning("Name is required");
    return false;
  }
  if ((destination === "mock_exam" || destination === "paper") && (!form.format_id || !form.school_year_id)) {
    toast.warning("Format and School Year are required");
    return false;
  }
  if (destination === "worksheet" && !form.topic_id) {
    toast.warning("Topic is required");
    return false;
  }
  return true;
};

/**
 * Save-approved-questions-to-bank dialog with a Mock Exam / Past Paper / Worksheet
 * destination picker. Shared by McqReview and SimilarPaperReview; the caller owns the
 * actual persist + confirm + create-entity calls in onSubmit.
 *
 * Props:
 *   open, busy, onClose, onSubmit
 *   description            — the info Alert body (defaults to a generic one)
 *   approvedCount          — number of questions being saved (for the default description)
 *   destination, onDestinationChange
 *   form, onFormChange     — {name, format_id, school_year_id, topic_id, price}
 *   categoryId, onCategoryChange
 *   lookups                — {formats, schoolYears, topics, loading}
 */
const SaveToBankDestinationDialog = ({
  open,
  busy,
  onClose,
  onSubmit,
  description,
  approvedCount,
  destination,
  onDestinationChange,
  form,
  onFormChange,
  categoryId,
  onCategoryChange,
  lookups,
}) => {
  const setField = (key) => (value) => onFormChange({ ...form, [key]: value });
  const { formats = [], schoolYears = [], topics = [], loading = false } = lookups || {};

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <AutoAwesomeIcon color="primary" />
        Save to Question Bank
      </DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          {description ||
            `${approvedCount} question${approvedCount === 1 ? "" : "s"} will be added to the question bank, then a draft ${CREATE_TARGET_LABELS[destination]?.toLowerCase()} is created from them. You'll be redirected to finish the rest of its settings.`}
        </Alert>

        <FormControl size="small" fullWidth sx={{ mb: 2 }} disabled={busy}>
          <InputLabel>Add to</InputLabel>
          <Select
            label="Add to"
            value={destination}
            onChange={(e) => onDestinationChange(e.target.value)}
          >
            <MenuItem value="mock_exam">Mock Exam</MenuItem>
            <MenuItem value="paper">Past Paper</MenuItem>
            <MenuItem value="worksheet">Worksheet</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="Name"
          size="small"
          fullWidth
          disabled={busy}
          value={form.name}
          onChange={(e) => setField("name")(e.target.value)}
          sx={{ mb: 2 }}
        />

        {(destination === "mock_exam" || destination === "paper") && (
          <>
            <FormControl size="small" fullWidth sx={{ mb: 2 }} disabled={busy}>
              <InputLabel>Format *</InputLabel>
              <Select
                label="Format *"
                value={form.format_id}
                onChange={(e) => setField("format_id")(e.target.value)}
              >
                {formats.map((fmt) => (
                  <MenuItem key={fmt.id} value={fmt.id}>{fmt.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth sx={{ mb: 2 }} disabled={busy}>
              <InputLabel>School Year *</InputLabel>
              <Select
                label="School Year *"
                value={form.school_year_id}
                onChange={(e) => setField("school_year_id")(e.target.value)}
              >
                {schoolYears.map((y) => (
                  <MenuItem key={y.id} value={y.id}>{y.name || y.year || y.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Price"
              type="number"
              size="small"
              disabled={busy}
              value={form.price}
              onChange={(e) => setField("price")(Math.max(0, +e.target.value))}
              inputProps={{ min: 0, step: "0.01" }}
              helperText="Defaults to 0 (free). Adjust later on the edit page."
              sx={{ width: 200, mb: 2 }}
            />
          </>
        )}

        {destination === "worksheet" && (
          <FormControl size="small" fullWidth sx={{ mb: 2 }} disabled={busy}>
            <InputLabel>Topic *</InputLabel>
            <Select
              label="Topic *"
              value={form.topic_id}
              onChange={(e) => setField("topic_id")(e.target.value)}
            >
              {topics.length === 0 && (
                <MenuItem value="" disabled>
                  {loading ? "Loading…" : "No topics found for this subject"}
                </MenuItem>
              )}
              {topics.map((t) => (
                <MenuItem key={t.id} value={t.id}>{t.label || t.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {loading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">Loading options…</Typography>
          </Box>
        )}

        <CategoryTreeSelect
          label="Category"
          value={categoryId ?? ""}
          onChange={onCategoryChange}
          size="small"
          fullWidth
          disabled={busy}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={busy}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
        >
          {busy ? "Saving…" : `Save & Create ${CREATE_TARGET_LABELS[destination] || ""}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SaveToBankDestinationDialog;
